import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { appliesQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";
import type { ProblemDetails } from "@/lib/helpers/handleProblemDetailsError";

const REDELIVER_ERROR_KEY_PREFIX = "integrations.cockpit.applies.detail.redeliver.errors";

/**
 * The refusals RedeliverApplyToCockpitCommandHandler can return. Each one is a different answer for the
 * operator — "activate the module first" and "the CV is gone" call for different next steps — so they are
 * translated separately rather than surfacing the backend's English message.
 */
const REDELIVER_REASON_BY_ERROR_CODE: Record<string, string | undefined> = {
  "RedeliverApplyToCockpit.AlreadyDelivered": "alreadyDelivered",
  "RedeliverApplyToCockpit.ClientUnknown": "clientUnknown",
  "RedeliverApplyToCockpit.Purged": "purged",
  "RedeliverApplyToCockpit.NoPublicationId": "noPublicationId",
  "RedeliverApplyToCockpit.OriginUnknown": "originUnknown",
  "RedeliverApplyToCockpit.AttachmentExpired": "attachmentExpired",
};

/** CockpitAppliesController puts a human title on 403/404 and the error code on the conflicts. */
const REDELIVER_REASON_BY_STATUS: Record<number, string | undefined> = {
  403: "moduleNotActive",
  404: "notFound",
};

export function redeliverApplyErrorKey(error: AxiosError<ProblemDetails>): string {
  const reason =
    REDELIVER_REASON_BY_ERROR_CODE[error.response?.data?.title ?? ""] ??
    REDELIVER_REASON_BY_STATUS[error.response?.status ?? 0] ??
    "unknown";

  return `${REDELIVER_ERROR_KEY_PREFIX}.${reason}`;
}

/**
 * Redelivers an apply Cockpit never received. The client is taken from the apply row server-side, so the
 * apply id is the whole request. Accepted (202) only queues the delivery — the match ID appears on a later
 * refetch, not in this response.
 */
export function useRedeliverApplyToCockpit(applyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError<ProblemDetails>>({
    mutationFn: async () => {
      if (!applyId) {
        throw new Error("applyId is required");
      }

      await instance.post(`/api/v2/Integrations/cockpit/applies/${applyId}/Redeliver`);
    },
    onSettled: () => {
      if (!applyId) return;

      queryClient.invalidateQueries({ queryKey: appliesQueryKeys.cockpit(applyId) });
    },
  });
}
